import { SelectableChip } from "@dropbox/dash-component-library";
import { Chip } from "@dropbox/dig-components/chip";
import { Box } from "@dropbox/dig-foundations";
import { useTheme } from "@mirage/service-settings/theming/useTheme";
import { SVGProps, useRef } from "react";

import { UIIcon } from "@dropbox/dig-icons";
import { useOmniboxMotionFlag } from "@mirage/mosaics/Omnibox/hooks/useOmniboxMotion";
import type { typeahead } from "@mirage/service-typeahead-search/service/types";
import {
  getIconVariant,
  LottieAnimation,
  LottieAnimationApi,
  LottieAnimationTypeWithoutVariant,
} from "@mirage/shared/lottie-animations/LottieAnimation";
import { useIsMobileSize } from "@mirage/shared/responsive/mobile";
import classNames from "classnames";
import styles from "./ScopedModeChip.module.css";

export type ScopedModeChipProps = {
  src: (props: SVGProps<SVGSVGElement>) => React.ReactElement;
  lottieAnimationType: LottieAnimationTypeWithoutVariant;
  chipOmniboxScopedMode: typeahead.Mode;
  chipLabel: string;
  onClickHandler: (modeType: typeahead.Mode) => void;
  disabled?: boolean;
  isSelected: boolean;
};

export const ScopedModeChip = ({
  src,
  chipLabel,
  onClickHandler,
  chipOmniboxScopedMode,
  isSelected,
  lottieAnimationType,
}: ScopedModeChipProps) => {
  const isMobile = useIsMobileSize();
  const { isChipsMotionV1, lottieApiRef, animationType } = useLottieChips(
    lottieAnimationType,
    isSelected,
  );

  return (
    <SelectableChip
      onClick={() => onClickHandler(chipOmniboxScopedMode)}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.code === "Enter") {
          onClickHandler(chipOmniboxScopedMode);
        }
      }}
      onMouseEnter={() => {
        lottieApiRef.current?.lottieRef?.goToAndPlay(0, true);
      }}
      size={isMobile ? "small" : "large"}
      isSelected={isSelected}
      selectedStyle="fill"
    >
      {isChipsMotionV1 ? (
        <Box
          className={classNames(styles.lottieAccessory, {
            [styles.isChipsMotionV1]: isChipsMotionV1,
          })}
        >
          <LottieAnimation
            ref={lottieApiRef}
            animationType={animationType}
            autoplay={false}
            loop={false}
            fallback={<UIIcon src={src} />}
          />
        </Box>
      ) : (
        <SelectableChip.LargeIconAccessory>
          <UIIcon src={src} />
        </SelectableChip.LargeIconAccessory>
      )}
      <Chip.Content>{chipLabel}</Chip.Content>
    </SelectableChip>
  );
};

function useLottieChips(
  lottieAnimationType: LottieAnimationTypeWithoutVariant,
  isSelected: boolean,
) {
  const lottieApiRef = useRef<LottieAnimationApi>(null);

  const isDarkMode = useTheme();
  const { getMotionVersionFor } = useOmniboxMotionFlag();
  const chipsMotionVariant = getMotionVersionFor("chips");

  let variant: "default" | "inverse" = "default";

  if (isSelected) {
    variant = isDarkMode ? "default" : "inverse";
  } else {
    variant = isDarkMode ? "inverse" : "default";
  }

  const animationType = getIconVariant(lottieAnimationType, variant);

  const isChipsMotionV1 = chipsMotionVariant === "V1";

  return {
    isChipsMotionV1,
    lottieApiRef,
    animationType,
  };
}
